import { inject, Injectable } from '@angular/core';
import { getUaDevice } from '@context/frontend/native/platform';
import { TranslocoService } from '@jsverse/transloco';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { DialogOptions } from '../dialogs';

@Injectable({ providedIn: 'root' })
export class DialogService {
	private readonly dialog = inject(TuiDialogService);
	private readonly transloco = inject(TranslocoService);

	open<T = undefined>(content: PolymorpheusContent, options: DialogOptions) {
		const label = options.label
			? this.transloco.translate(options.label)
			: undefined;

		const merged: any = Object.assign({}, options, { label });

		/**
		 * Taiga v5 no longer auto-expands dialogs on smaller viewports the way
		 * v4's mobile addon did. Restore that behavior centrally: any dialog that
		 * hasn't explicitly chosen an appearance becomes fullscreen on non-desktop.
		 * A dialog can opt out by setting its own `appearance` (e.g. 'taiga').
		 *
		 * Only assign `appearance` when we have a concrete value. Passing
		 * `appearance: undefined` would spread over (and clobber) Taiga's own
		 * default of 'taiga', leaving the dialog context with an undefined
		 * appearance - which crashes its host bindings (`appearance.includes(...)`).
		 */
		const appearance =
			options.appearance ??
			(getUaDevice() !== 'desktop' ? 'fullscreen' : undefined);

		if (appearance) merged.appearance = appearance;
		else delete merged.appearance;

		return this.dialog.open<T>(content, merged);
	}
}
